(function () {
  "use strict";

  const root = window.LegitGrails = window.LegitGrails || {};
  const QUEUE_KEY = "preFaroTelemetry";
  const QUEUE_LIMIT = 50;
  let collectionAllowed = true;

  /**
   * Returns the Faro-backed telemetry facade when faro-observability.js has
   * already installed it.
   *
   * @returns {object|null}
   */
  function getTelemetryFacade() {
    return root.telemetry && typeof root.telemetry === "object"
      ? root.telemetry
      : null;
  }

  /**
   * Returns whether the Faro facade can accept telemetry immediately.
   *
   * @param {object|null} telemetry
   * @returns {boolean}
   */
  function canForwardToTelemetry(telemetry) {
    if (!collectionAllowed) return false;
    if (!telemetry) return false;
    if (typeof telemetry.isEnabled !== "function") return true;
    return telemetry.isEnabled();
  }

  /**
   * Clears all telemetry captured before Faro became ready.
   *
   * @returns {void}
   */
  function clearQueue() {
    if (Array.isArray(root[QUEUE_KEY])) {
      root[QUEUE_KEY].length = 0;
    }
  }

  /**
   * Enables or disables bridge collection.
   *
   * When disabled by future Cookie Consent, the bridge drops new telemetry and
   * clears already queued calls so denied users cannot send delayed Faro data.
   *
   * @param {boolean} isAllowed
   * @returns {void}
   */
  function setCollectionAllowed(isAllowed) {
    collectionAllowed = Boolean(isAllowed);

    if (!collectionAllowed) {
      clearQueue();
    }
  }

  /**
   * Stores telemetry calls made before the Faro runtime is ready.
   *
   * The queue is intentionally bounded and in-memory only. It preserves early
   * storefront errors during the current page load and is not durable storage.
   *
   * @param {{type: "event"|"error", name?: string, attributes?: object, domain?: string, error?: *, context?: object}} item
   * @returns {void}
   */
  function enqueue(item) {
    if (!collectionAllowed) return;

    const queue = Array.isArray(root[QUEUE_KEY])
      ? root[QUEUE_KEY]
      : [];

    queue.push(item);
    if (queue.length > QUEUE_LIMIT) {
      queue.shift();
    }

    root[QUEUE_KEY] = queue;
  }

  /**
   * Drains telemetry calls queued before Faro became ready.
   *
   * @returns {Array<object>}
   */
  function drainQueue() {
    if (!collectionAllowed) {
      clearQueue();
      return [];
    }

    return Array.isArray(root[QUEUE_KEY])
      ? root[QUEUE_KEY].splice(0)
      : [];
  }

  /**
   * Creates a trace-friendly client-side identifier.
   *
   * @param {string} prefix
   * @returns {string}
   */
  function createId(prefix) {
    const telemetry = getTelemetryFacade();
    if (telemetry && typeof telemetry.createId === "function") {
      return telemetry.createId(prefix);
    }

    const cryptoApi = window.crypto || window.msCrypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
      return prefix + "_" + cryptoApi.randomUUID();
    }

    return prefix + "_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 12);
  }

  /**
   * Sends a custom event through Faro when available, otherwise queues it.
   *
   * @param {string} name
   * @param {object} [attributes]
   * @param {string} [domain]
   * @returns {void}
   */
  function pushEvent(name, attributes, domain) {
    try {
      if (!collectionAllowed) return;

      const telemetry = getTelemetryFacade();
      if (canForwardToTelemetry(telemetry) && typeof telemetry.pushEvent === "function") {
        telemetry.pushEvent(name, attributes, domain);
        return;
      }

      enqueue({
        type: "event",
        name,
        attributes,
        domain,
      });
    } catch (error) {
      // Telemetry must never break the storefront.
    }
  }

  /**
   * Sends an exception through Faro when available, otherwise queues it.
   *
   * @param {*} error
   * @param {object} [context]
   * @returns {void}
   */
  function pushError(error, context) {
    try {
      if (!collectionAllowed) return;

      const telemetry = getTelemetryFacade();
      if (canForwardToTelemetry(telemetry) && typeof telemetry.pushError === "function") {
        telemetry.pushError(error, context);
        return;
      }

      enqueue({
        type: "error",
        error,
        context,
      });
    } catch (telemetryError) {
      // Telemetry must never break the storefront.
    }
  }

  /**
   * Adds common storefront context to theme-level telemetry payloads.
   *
   * @param {object} [context]
   * @returns {object}
   */
  function buildThemeContext(context) {
    return Object.assign({
      path: window.location ? window.location.pathname : "",
    }, context || {});
  }

  /**
   * Sends a theme-level warning event through the shared telemetry bridge.
   *
   * @param {string} phase
   * @param {object} [context]
   * @returns {void}
   */
  function pushThemeWarning(phase, context) {
    pushEvent("shopify_theme_warning", buildThemeContext(Object.assign({
      phase,
    }, context || {})), "shopify_theme");
  }

  /**
   * Sends a theme-level exception through the shared telemetry bridge.
   *
   * @param {string|Error} error
   * @param {object} [context]
   * @returns {void}
   */
  function pushThemeError(error, context) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    pushError(normalizedError, buildThemeContext(context));
  }

  root.telemetryBridge = {
    createId,
    drainQueue,
    pushEvent,
    pushError,
    pushThemeError,
    pushThemeWarning,
    setCollectionAllowed,
  };
})();
